import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
/**
 * Formats a number as Indonesian Rupiah (IDR).
 * @param {number} amount The number to format.
 * @returns {string} The formatted currency string, e.g., "Rp1.500.000".
 */
export const formatRupiah = (amount) => {
  if (amount === null || typeof amount === 'undefined' || Number.isNaN(Number(amount))) {
    return '-';
  }
  
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
  }).format(amount);
};

export const formatRupiahTerbilang = (number) => {
  const satuan = [
    "", "Satu", "Dua", "Tiga", "Empat", "Lima",
    "Enam", "Tujuh", "Delapan", "Sembilan", "Sepuluh", "Sebelas"
  ];

  number = Math.floor(number);

  const toWords = (n) => {
    if (n < 12) return satuan[n];
    if (n < 20) return toWords(n - 10) + " Belas";
    if (n < 100) return toWords(Math.floor(n / 10)) + " Puluh " + toWords(n % 10);
    if (n < 200) return "Seratus " + toWords(n - 100);
    if (n < 1000) return toWords(Math.floor(n / 100)) + " Ratus " + toWords(n % 100);
    if (n < 2000) return "Seribu " + toWords(n - 1000);
    if (n < 1000000) return toWords(Math.floor(n / 1000)) + " Ribu " + toWords(n % 1000);
    if (n < 1000000000) return toWords(Math.floor(n / 1000000)) + " Juta " + toWords(n % 1000000);
    if (n < 1000000000000) return toWords(Math.floor(n / 1000000000)) + " Milyar " + toWords(n % 1000000000);
    if (n < 1000000000000000) return toWords(Math.floor(n / 1000000000000)) + " Triliun " + toWords(n % 1000000000000);
    return "";
  };

  const result = toWords(number).replace(/\s+/g, " ").trim();
  return result ? `Terbilang : # ${result} Rupiah #` : "";
};

export const exportLedgerToExcel = (transactions = [], summary = {}, startDate, endDate) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Buku Besar");

  // ===== HEADER UTAMA =====
  worksheet.mergeCells("A1:M1");
  worksheet.getCell("A1").value = "Buku Besar";
  worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getCell("A1").font = { bold: true, size: 16 };

  worksheet.mergeCells("A2:M2");
  worksheet.getCell("A2").value = `Periode ${startDate} - ${endDate}`;
  worksheet.getCell("A2").alignment = { horizontal: "center" };
  worksheet.getCell("A2").font = { italic: true, size: 12 };

  // Add account info if available
  if (summary.account_code && summary.account_name) {
    worksheet.mergeCells("A3:M3");
    worksheet.getCell("A3").value = `Perkiraan: ${summary.account_code} - ${summary.account_name}`;
    worksheet.getCell("A3").alignment = { horizontal: "center" };
    worksheet.getCell("A3").font = { bold: true, size: 12 };
  }

  worksheet.addRow([]);

  // ===== HEADER TABEL =====
  const headers = [
    "No",
    "Tanggal",
    "No Slip",
    "Perkiraan",
    "Keterangan",
    "Debet",
    "Kredit",
    "Saldo",
    "Nama",
    "No Agt",
    "Norek",
    "Muid",
    "Opr",
  ];
  const headerRowIndex = worksheet.lastRow.number + 1;
  worksheet.addRow(headers);

  headers.forEach((_, i) => {
    const cell = worksheet.getRow(headerRowIndex).getCell(i + 1);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "1E3A8A" } };
    cell.font = { bold: true, color: { argb: "FFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
    cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
  });

  // ===== DATA TRANSAKSI (includes SALDO AWAL as first row) =====
  // Filter out the SALDO AWAL row for separate handling
  const dataTransactions = transactions.filter(t => t.row_number > 0); // Skip SALDO AWAL (row_number: 0)
  const openingBalance = Math.abs(summary.opening_balance || 0);

  // Add SALDO AWAL row
  const saldoRow = worksheet.addRow([
    "",
    "",
    "",
    "",
    "SALDO AWAL",
    "",
    "",
    openingBalance,
    "",
    "",
    "",
    "",
    "",
  ]);

  saldoRow.eachCell((cell, idx) => {
    cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
    cell.font = { bold: true };
    if (idx === 5) {
      cell.alignment = { horizontal: "center" };
    } else if (idx === 8) {
      cell.alignment = { horizontal: "right" };
      cell.numFmt = "#,##0";
    }
  });

  // Add transaction rows
  dataTransactions.forEach((trx, i) => {
    const row = worksheet.addRow([
      i + 1,
      new Date(trx.transaction_date).toLocaleDateString("id-ID"),
      trx.journal_number || "-",
      `${trx.account_code} ${trx.account_name}`,
      trx.journal_description || "-",
      Math.abs(trx.debit_amount || 0),
      Math.abs(trx.credit_amount || 0),
      Math.abs(trx.balance || 0),
      trx.member_name || "-",
      trx.member_number || "-",
      trx.account_number || "-",
      trx.reference_type || "-",
      trx.created_by_operator || "-",
    ]);

    row.eachCell((cell, idx) => {
      cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
      if ([6, 7, 8].includes(idx)) {
        cell.alignment = { horizontal: "right" };
        cell.numFmt = "#,##0";
      } else {
        cell.alignment = { horizontal: "left" };
      }
    });
  });

  // ===== TOTAL =====
  const totalDebit = Math.abs(summary.total_debit || 0);
  const totalCredit = Math.abs(summary.total_credit || 0);
  const closingBalance = Math.abs(summary.closing_balance || 0);

  const totalRowIndex = worksheet.lastRow.number + 1;
  worksheet.mergeCells(`A${totalRowIndex}:E${totalRowIndex}`);
  worksheet.getCell(`A${totalRowIndex}`).value = "Total";
  worksheet.getCell(`A${totalRowIndex}`).alignment = { horizontal: "center" };
  worksheet.getCell(`A${totalRowIndex}`).font = { bold: true };

  worksheet.getCell(`F${totalRowIndex}`).value = totalDebit;
  worksheet.getCell(`G${totalRowIndex}`).value = totalCredit;
  worksheet.getCell(`H${totalRowIndex}`).value = closingBalance;

  ["F", "G", "H"].forEach((col) => {
    const cell = worksheet.getCell(`${col}${totalRowIndex}`);
    cell.alignment = { horizontal: "right" };
    cell.numFmt = "#,##0";
    cell.font = { bold: true };
  });

  worksheet.getRow(totalRowIndex).eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F3F4F6" } };
    cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
    if (!cell.font?.bold) cell.font = { bold: true };
  });

  // ===== AUTO WIDTH =====
  worksheet.columns.forEach((col) => {
    let maxLength = 0;
    col.eachCell({ includeEmpty: true }, (cell) => {
      const len = cell.value ? cell.value.toString().length : 10;
      if (len > maxLength) maxLength = len;
    });
    col.width = maxLength + 2;
  });

  // ===== EXPORT FILE =====
  const accountInfo = summary.account_code ? `-${summary.account_code}` : '';
  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer]), `Buku-Besar${accountInfo}-${startDate}-${endDate}.xlsx`);
  });
};

export const exportCashBookToExcel = (report, startDate, endDate) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Buku Kas Harian");

  const summary = report.summary || {};
  const transactions = report.transactions || [];

  // Judul utama
  worksheet.mergeCells("A1:H1");
  worksheet.getCell("A1").value = "Buku Kas";
  worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getCell("A1").font = { bold: true, size: 16 };

  // Periode
  worksheet.mergeCells("A2:H2");
  worksheet.getCell("A2").value = `Periode ${startDate} - ${endDate}`;
  worksheet.getCell("A2").alignment = { horizontal: "center" };
  worksheet.getCell("A2").font = { italic: true, size: 12 };

  worksheet.addRow([]);

  // Header
  const headerRow = ["No", "Tanggal", "Nomor Slip", "Perkiraan", "Keterangan", "Debet", "Kredit", "Saldo"];
  worksheet.addRow(headerRow);

  // Styling header
  headerRow.forEach((_, idx) => {
    const cell = worksheet.getRow(4).getCell(idx + 1);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "1E3A8A" } };
    cell.font = { bold: true, color: { argb: "FFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // SALDO SEBELUMNYA
  const saldoAwalIndex = worksheet.lastRow.number + 1;
  worksheet.mergeCells(`A${saldoAwalIndex}:G${saldoAwalIndex}`);
  worksheet.getCell(`A${saldoAwalIndex}`).value = "SALDO SEBELUMNYA";
  worksheet.getCell(`A${saldoAwalIndex}`).alignment = { horizontal: "center" };
  worksheet.getCell(`A${saldoAwalIndex}`).font = { bold: true };

  worksheet.getCell(`H${saldoAwalIndex}`).value = summary.opening_balance || 0;
  worksheet.getCell(`H${saldoAwalIndex}`).numFmt = "#,##0";
  worksheet.getCell(`H${saldoAwalIndex}`).alignment = { horizontal: "right" };

  worksheet.getRow(saldoAwalIndex).eachCell((cell) => {
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // DATA
  let runningSaldo = summary.opening_balance || 0;

  transactions.forEach((trx, idx) => {
    runningSaldo += (trx.debit_amount || 0) - (trx.credit_amount || 0);

    const row = worksheet.addRow([
      idx + 1,
      new Date(trx.transaction_date).toLocaleDateString("id-ID"),
      trx.journal_number || "-",
      `${trx.account_code || ""} ${trx.account_name || ""}`,
      trx.journal_description || trx.description || "-",
      trx.debit_amount || 0,
      trx.credit_amount || 0,
      runningSaldo,
    ]);

    row.eachCell((cell, colNumber) => {
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
      if (colNumber >= 6) {
        cell.alignment = { horizontal: "right" };
        cell.numFmt = "#,##0";
      }
    });
  });

  // TOTAL ROW
  const totalDebit = transactions.reduce((s, t) => s + (t.debit_amount || 0), 0);
  const totalKredit = transactions.reduce((s, t) => s + (t.credit_amount || 0), 0);
  const totalSaldo = summary.closing_balance || runningSaldo;

  const totalRowIndex = worksheet.lastRow.number + 1;
  worksheet.mergeCells(`A${totalRowIndex}:E${totalRowIndex}`);
  worksheet.getCell(`A${totalRowIndex}`).value = "Total";
  worksheet.getCell(`A${totalRowIndex}`).alignment = { horizontal: "center" };
  worksheet.getCell(`A${totalRowIndex}`).font = { bold: true };

  worksheet.getCell(`F${totalRowIndex}`).value = totalDebit;
  worksheet.getCell(`F${totalRowIndex}`).numFmt = "#,##0";
  worksheet.getCell(`F${totalRowIndex}`).alignment = { horizontal: "right" };

  worksheet.getCell(`G${totalRowIndex}`).value = totalKredit;
  worksheet.getCell(`G${totalRowIndex}`).numFmt = "#,##0";
  worksheet.getCell(`G${totalRowIndex}`).alignment = { horizontal: "right" };

  worksheet.getCell(`H${totalRowIndex}`).value = totalSaldo;
  worksheet.getCell(`H${totalRowIndex}`).numFmt = "#,##0";
  worksheet.getCell(`H${totalRowIndex}`).alignment = { horizontal: "right" };

  worksheet.getRow(totalRowIndex).eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "F3F4F6" } };
    cell.font = { bold: true };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Auto width
  worksheet.columns.forEach((col) => {
    let maxLength = 0;
    col.eachCell({ includeEmpty: true }, (cell) => {
      const length = cell.value ? cell.value.toString().length : 10;
      if (length > maxLength) maxLength = length;
    });
    col.width = maxLength + 2;
  });

  // Export file
  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer]), `Buku-Kas-Harian-${startDate}-${endDate}.xlsx`);
  });
};

export const exportBalanceSheetToExcel = (data = [], cooperativeName = "", asOfDate = "") => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Neraca");

  // Group accounts by main category
  const grouped = {
    "Aktiva Lancar": data.filter(
      (acc) =>
        acc.account_code.startsWith("1.0.") ||
        acc.account_code.startsWith("1.1.") ||
        acc.account_code === "1.0.00" ||
        acc.account_code === "1.1.00"
    ),
    "Aktiva Tetap": data.filter(
      (acc) =>
        acc.account_code.startsWith("1.2.") ||
        acc.account_code === "1.2.00"
    ),
    Hutang: data.filter((acc) => acc.account_code.startsWith("2")),
    Modal: data.filter((acc) => acc.account_code.startsWith("3")),
  };

  // Calculate totals per group
  const totalPerGroup = (items) => {
    const debit = items.reduce((sum, i) => sum + (i.debit_balance || 0), 0);
    const credit = items.reduce((sum, i) => sum + (i.credit_balance || 0), 0);
    const closing = debit - credit;
    return { debit, credit, closing };
  };

  const totalAktivaLancar = totalPerGroup(grouped["Aktiva Lancar"]);
  const totalAktivaTetap = totalPerGroup(grouped["Aktiva Tetap"]);
  const totalAktiva = {
    debit: totalAktivaLancar.debit + totalAktivaTetap.debit,
    credit: totalAktivaLancar.credit + totalAktivaTetap.credit,
    closing: totalAktivaLancar.closing + totalAktivaTetap.closing,
  };

  const totalHutang = totalPerGroup(grouped.Hutang);
  const totalModal = totalPerGroup(grouped.Modal);
  const totalPassiva = {
    debit: totalHutang.debit + totalModal.debit,
    credit: totalHutang.credit + totalModal.credit,
    closing: totalHutang.closing + totalModal.closing,
  };

  // ===== HEADER UTAMA =====
  worksheet.mergeCells("A1:F1");
  worksheet.getCell("A1").value = cooperativeName || "Koperasi";
  worksheet.getCell("A1").alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getCell("A1").font = { bold: true, size: 14 };

  worksheet.mergeCells("A2:F2");
  worksheet.getCell("A2").value = "Neraca";
  worksheet.getCell("A2").alignment = { horizontal: "center", vertical: "middle" };
  worksheet.getCell("A2").font = { bold: true, size: 16 };

  worksheet.mergeCells("A3:F3");
  worksheet.getCell("A3").value = `per ${asOfDate}`;
  worksheet.getCell("A3").alignment = { horizontal: "center" };
  worksheet.getCell("A3").font = { italic: true, size: 12 };

  worksheet.addRow([]);

  // ===== HEADER TABEL =====
  const headerRow1 = worksheet.addRow(["Perkiraan", "", "Saldo Awal", "Debit", "Kredit", "Saldo Akhir"]);
  worksheet.mergeCells(`A${headerRow1.number}:B${headerRow1.number}`);

  headerRow1.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "1E3A8A" } };
    cell.font = { bold: true, color: { argb: "FFFFFF" } };
    cell.alignment = { horizontal: "center", vertical: "middle" };
    cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
  });

  const headerRow2 = worksheet.addRow(["Kode", "Nama Perkiraan", "Rp", "Rp", "Rp", "Rp"]);
  headerRow2.eachCell((cell, idx) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "1E40AF" } };
    cell.font = { bold: true, color: { argb: "FFFFFF" } };
    cell.alignment = { horizontal: idx === 1 ? "center" : idx === 2 ? "left" : "right", vertical: "middle" };
    cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
  });

  // Helper function to add account rows
  const addAccountRows = (accounts) => {
    accounts.forEach((acc) => {
      const debit = Math.abs(acc.debit_balance || 0);
      const credit = Math.abs(acc.credit_balance || 0);
      const closing = Math.abs((acc.debit_balance || 0) - (acc.credit_balance || 0));

      const row = worksheet.addRow([
        acc.account_code,
        acc.account_name,
        0,
        debit,
        credit,
        closing
      ]);

      row.eachCell((cell, idx) => {
        cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
        if (idx === 1) {
          cell.alignment = { horizontal: "center" };
          cell.font = { name: "Courier New" };
        } else if (idx === 2) {
          cell.alignment = { horizontal: "left" };
        } else {
          cell.alignment = { horizontal: "right" };
          cell.numFmt = "#,##0.00";
        }
      });
    });
  };

  // Helper function to add subtotal row
  const addSubtotalRow = (label, totals, bgColor = "DBEAFE") => {
    const row = worksheet.addRow([
      "",
      label,
      0,
      Math.abs(totals.debit),
      Math.abs(totals.credit),
      Math.abs(totals.closing)
    ]);

    row.eachCell((cell, idx) => {
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
      cell.font = { bold: true };
      cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
      if (idx <= 2) {
        cell.alignment = { horizontal: "right" };
      } else {
        cell.alignment = { horizontal: "right" };
        cell.numFmt = "#,##0.00";
      }
    });

    worksheet.mergeCells(`A${row.number}:B${row.number}`);
  };

  // Helper function to add section header
  const addSectionHeader = (label, bgColor = "EFF6FF") => {
    const row = worksheet.addRow([label, "", "", "", "", ""]);
    worksheet.mergeCells(`A${row.number}:F${row.number}`);

    const cell = worksheet.getCell(`A${row.number}`);
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
    cell.font = { bold: true, size: 12 };
    cell.alignment = { horizontal: "left", vertical: "middle" };
    cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
  };

  // ===== AKTIVA LANCAR =====
  addSectionHeader("Aktiva Lancar", "DBEAFE");
  addAccountRows(grouped["Aktiva Lancar"]);
  addSubtotalRow("Total Aktiva Lancar", totalAktivaLancar, "BFDBFE");
  worksheet.addRow([]);

  // ===== AKTIVA TETAP =====
  addSectionHeader("Aktiva Tetap", "DBEAFE");
  addAccountRows(grouped["Aktiva Tetap"]);
  addSubtotalRow("Total Aktiva Tetap", totalAktivaTetap, "BFDBFE");
  worksheet.addRow([]);

  // ===== TOTAL AKTIVA =====
  const totalAktivaRow = worksheet.addRow([
    "",
    "TOTAL AKTIVA",
    0,
    Math.abs(totalAktiva.debit),
    Math.abs(totalAktiva.credit),
    Math.abs(totalAktiva.closing)
  ]);

  worksheet.mergeCells(`A${totalAktivaRow.number}:B${totalAktivaRow.number}`);
  totalAktivaRow.eachCell((cell, idx) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "93C5FD" } };
    cell.font = { bold: true, size: 13 };
    cell.border = { top:{style:'medium'}, left:{style:'medium'}, bottom:{style:'medium'}, right:{style:'medium'} };
    if (idx <= 2) {
      cell.alignment = { horizontal: "right", vertical: "middle" };
    } else {
      cell.alignment = { horizontal: "right", vertical: "middle" };
      cell.numFmt = "#,##0.00";
    }
  });
  worksheet.addRow([]);

  // ===== HUTANG =====
  addSectionHeader("Hutang / Liabilitas", "FEE2E2");
  addAccountRows(grouped.Hutang);
  addSubtotalRow("Total Hutang", {
    debit: totalHutang.debit,
    credit: totalHutang.credit,
    closing: totalHutang.credit - totalHutang.debit
  }, "FECACA");
  worksheet.addRow([]);

  // ===== MODAL =====
  addSectionHeader("Modal / Ekuitas", "D1FAE5");
  addAccountRows(grouped.Modal);
  addSubtotalRow("Total Modal", {
    debit: totalModal.debit,
    credit: totalModal.credit,
    closing: totalModal.credit - totalModal.debit
  }, "A7F3D0");
  worksheet.addRow([]);

  // ===== TOTAL PASSIVA =====
  const totalPassivaRow = worksheet.addRow([
    "",
    "TOTAL PASIVA",
    0,
    Math.abs(totalPassiva.debit),
    Math.abs(totalPassiva.credit),
    Math.abs(totalPassiva.credit - totalPassiva.debit)
  ]);

  worksheet.mergeCells(`A${totalPassivaRow.number}:B${totalPassivaRow.number}`);
  totalPassivaRow.eachCell((cell, idx) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "C084FC" } };
    cell.font = { bold: true, size: 13 };
    cell.border = { top:{style:'medium'}, left:{style:'medium'}, bottom:{style:'medium'}, right:{style:'medium'} };
    if (idx <= 2) {
      cell.alignment = { horizontal: "right", vertical: "middle" };
    } else {
      cell.alignment = { horizontal: "right", vertical: "middle" };
      cell.numFmt = "#,##0.00";
    }
  });

  // ===== AUTO WIDTH =====
  worksheet.columns = [
    { width: 12 },  // Kode
    { width: 35 },  // Nama Perkiraan
    { width: 15 },  // Saldo Awal
    { width: 15 },  // Debit
    { width: 15 },  // Kredit
    { width: 15 },  // Saldo Akhir
  ];

  // ===== EXPORT FILE =====
  workbook.xlsx.writeBuffer().then((buffer) => {
    saveAs(new Blob([buffer]), `Neraca-${asOfDate}.xlsx`);
  });
};
